/**
 * Editor empty-state CONTENT — the build-time-dynamic projection of the
 * supported-editor-format registry onto the `/editor` empty-state hero.
 *
 * bug2-editor-emptystate-dynamic-from-ssot (Epic:
 * libreoffice-odf-support-via-uno-260819). The empty-state hero used to carry a
 * HARDCODED icon row + facts list in index.html — which is how `.odp` shipped
 * as a supported editor format but never appeared in the hero. This module now
 * renders the empty-state surfaces from two side-effect-free sources:
 *
 *   1. the "Supported file types" region (`#editor-empty-iconrow`) — the BY-KIND
 *      bubbles (Documents / Presentations / Spreadsheets / PDF), rendered via the
 *      SHARED `shared/supported-format-bubbles.ts` from the codegen'd
 *      `ADVERTISED_BY_KIND` (generated/advertised-formats.ts) — the SAME source the
 *      main SPA upload target uses, so the grouping is defined ONCE (D1,
 *      frontend-knobs-and-visuals-redesign-260901).
 *   2. the "Drop a … file" lead-in (`#editor-empty-sub-lead`) — editor-ACCEPT truth
 *   3. the per-format facts list (`#editor-empty-facts`) — editor-ACCEPT truth
 *
 * Surfaces 2 + 3 are a PROJECTION of `EDITOR_ROUTE_BY_EXT` (upload-routing.ts, the
 * editor-accept routing source of truth, §3.2): adding a format there (with its
 * `label`/`emptyStateFact` descriptor — the compiler forces both) auto-populates
 * the lead-in + facts; no HTML edit. Surface 1 shows the broader PRODUCT support
 * surface (incl. CLI-only LaTeX), decoupled from the editor-accept registry. Each
 * bubble `iconId` needs a matching inline `<symbol>` in editor/index.html —
 * drift-guarded by tools/lint/lint-editor-emptystate-ssot-drift.py.
 *
 * Pure DOM builder: imports ONLY side-effect-free seams (the routing registry +
 * the shared bubbles renderer), so it is independently unit-testable in jsdom
 * (test/editor-empty-state-content.jsdom.test.ts).
 */
import { EDITOR_ROUTE_BY_EXT } from "./upload-routing.js";
import { renderSupportedFormatBubbles } from "../shared/supported-format-bubbles.js";
/** Ordered registry rows — the render order for the lead-in + facts surfaces. */
function _entries() {
    return Object.values(EDITOR_ROUTE_BY_EXT);
}
/**
 * "PDF, PowerPoint, Word, Excel, or OpenDocument Presentation" — the
 * human-readable format list for the empty-state lead-in, joined with an Oxford
 * "or". Single item → itself; two items → "A or B".
 */
function _joinLabels(labels) {
    if (labels.length <= 1)
        return labels.join("");
    if (labels.length === 2)
        return `${labels[0]} or ${labels[1]}`;
    const last = labels[labels.length - 1];
    return `${labels.slice(0, -1).join(", ")}, or ${last}`;
}
/** Build one facts `<li>`: `<strong>Label</strong> — <fact>`. */
function _factListItem(entry) {
    const li = document.createElement("li");
    const strong = document.createElement("strong");
    strong.textContent = entry.label;
    li.appendChild(strong);
    // " — " is " — " (em dash), matching the prior hardcoded copy.
    li.appendChild(document.createTextNode(` — ${entry.emptyStateFact}`));
    return li;
}
/**
 * Render the empty-state icon row, lead-in, and facts list from the registry.
 *
 * Idempotent — clears and re-fills the containers, so a re-call (e.g. after a
 * hot-reload) doesn't duplicate rows. No-ops on any container that isn't present
 * (defensive for partial test DOM scaffolds); the served-page smoke gate + the
 * drift lint are the real guards that the containers + symbols exist.
 */
export function renderEmptyStateContent() {
    const entries = _entries();
    // The "Supported file types" region is the by-kind bubbles, rendered from
    // the SHARED codegen'd SSOT (ADVERTISED_BY_KIND) — the SAME module the main
    // SPA upload target uses, so the grouping is defined in one place (D1). The
    // editor-ACCEPT truth stays in the lead-in + facts below.
    const iconrow = document.getElementById("editor-empty-iconrow");
    if (iconrow) {
        renderSupportedFormatBubbles(iconrow);
    }
    const lead = document.getElementById("editor-empty-sub-lead");
    if (lead) {
        lead.textContent = `Drop a ${_joinLabels(entries.map((e) => e.label))} file, or `;
    }
    const facts = document.getElementById("editor-empty-facts");
    if (facts) {
        facts.replaceChildren(...entries.map(_factListItem));
    }
}
